import React, { useState } from 'react';
import { CountryRank } from '../types';
import { ArrowUp, ArrowDown, Minus, ChevronRight, X } from 'lucide-react';

interface CountryRankingsProps {
  rankings: CountryRank[];
}

export const CountryRankings: React.FC<CountryRankingsProps> = ({ rankings = [] }) => {
  const [selectedCountry, setSelectedCountry] = useState<CountryRank | null>(null);
  const safeRankings = Array.isArray(rankings) ? rankings : [];

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Global Footprint</span>
          <h3 className="text-lg font-bold text-slate-900 tracking-tight">Rankings by Country</h3>
        </div>
        <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100">
          {safeRankings.length} Key Storefronts
        </span>
      </div>

      <div className="divide-y divide-slate-100">
        {safeRankings.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-500 font-medium">
            No country ranking data available
          </div>
        ) : (
          safeRankings.map((c) => {
          const isUp = c.change > 0;
          const isDown = c.change < 0;
          return (
            <div
              key={c.countryCode}
              onClick={() => setSelectedCountry(c)}
              className="py-3 px-2 flex items-center justify-between hover:bg-slate-50/80 rounded-2xl transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl bg-slate-100 border border-slate-200/80 text-slate-800 font-bold text-[11px] flex items-center justify-center flex-shrink-0">
                  {c.countryCode}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    {c.countryName}
                  </h4>
                  <span className="text-xs text-slate-500">{c.category}</span>
                </div>
              </div>

              <div className="text-right flex items-center gap-4">
                <div className="text-right">
                  <div className="text-base font-extrabold text-slate-900">
                    #{c.rank}
                  </div>
                  <div className="text-[11px] font-semibold flex items-center justify-end gap-1">
                    {isUp && (
                      <span className="inline-flex items-center gap-0.5 text-emerald-600">
                        <ArrowUp className="w-3 h-3" strokeWidth={2.5} />
                        <span>+{c.change} pos</span>
                      </span>
                    )}
                    {isDown && (
                      <span className="inline-flex items-center gap-0.5 text-rose-500">
                        <ArrowDown className="w-3 h-3" strokeWidth={2.5} />
                        <span>{Math.abs(c.change)} pos</span>
                      </span>
                    )}
                    {c.change === 0 && (
                      <span className="inline-flex items-center gap-0.5 text-slate-400">
                        <Minus className="w-3 h-3" strokeWidth={2} />
                        <span>Stable</span>
                      </span>
                    )}
                  </div>
                </div>

                <ChevronRight className="w-4 h-4 text-slate-300" strokeWidth={2} />
              </div>
            </div>
          );
        })
        )}
      </div>

      {selectedCountry && (
        <div className="mt-4 p-4 rounded-2xl bg-slate-50 border border-slate-200 text-xs flex items-center justify-between animate-fadeIn">
          <div>
            <span className="font-bold text-slate-900">[{selectedCountry.countryCode}] {selectedCountry.countryName} Spotlight:</span>
            <span className="text-slate-600 ml-1.5">
              Ranked #{selectedCountry.rank} with previous benchmark #{selectedCountry.previousRank}.
            </span>
          </div>
          <button
            onClick={() => setSelectedCountry(null)}
            className="text-slate-400 hover:text-slate-700 p-1 rounded hover:bg-slate-200 transition-colors ml-2"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
      )}
    </div>
  );
};
